/**
 * Authentication API for wallet-based login
 */

import { getApiUrl } from './config';

const API_BASE = '/api/auth';
const TOKEN_KEY = 'tezos_marketplace_token';

/**
 * Helper to build full API URL
 */
function authUrl(path: string): string {
  return getApiUrl(`${API_BASE}${path}`);
}

export interface AuthNonceResponse {
  nonce: string;
  message: string;
  issuedAt: string;
  expiresAt: string;
}

export interface AuthVerifyResponse {
  token: string;
  wallet: string;
  expiresIn: number;
}

/**
 * Get a nonce for signing
 */
export async function requestNonce(walletAddress: string): Promise<AuthNonceResponse> {
  const response = await fetch(authUrl('/nonce'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ walletAddress }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to get nonce' }));
    throw new Error(error.error || 'Failed to get nonce');
  }

  return response.json();
}

/**
 * Verify a signed message and get a JWT
 */
export async function verifySignature(
  walletAddress: string,
  publicKey: string,
  signature: string,
  nonce: string
): Promise<AuthVerifyResponse> {
  const response = await fetch(authUrl('/verify'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      walletAddress,
      publicKey,
      signature,
      nonce,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to verify signature' }));
    throw new Error(error.error || 'Failed to verify signature');
  }

  return response.json();
}

/**
 * Store token in localStorage
 */
export function storeToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

/**
 * Get stored token
 */
export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Clear stored token
 */
export function clearStoredToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * Check if token is expired
 */
export function isTokenExpired(token: string): boolean {
  try {
    // JWT format: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) return true;

    const payload = JSON.parse(atob(parts[1]));
    const exp = payload.exp;
    if (!exp) return true;

    // Check if expired (with 5 minute buffer)
    return Date.now() >= (exp * 1000) - (5 * 60 * 1000);
  } catch {
    return true;
  }
}

/**
 * Get auth header for API requests
 */
export function getAuthHeader(): Record<string, string> {
  const token = getStoredToken();
  if (!token || isTokenExpired(token)) {
    return {};
  }
  return { Authorization: `Bearer ${token}` };
}
